import {
  Box,
  Button,
  Chip,
  Grid,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../../components/Header";
import { authFetch } from "../../Utils/apiClient";

function ViewDeliver() {
  const { id } = useParams();
  const [memberDetails, setMemberDetails] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadDetails = async () => {
    try {
      const response = await authFetch(`/user/getUserById/${id}`);
      const memberData = await response.json();
      if (!response.ok || !memberData.result) {
        throw new Error(memberData.message || "Failed to fetch delivery user");
      }

      const {
        email,
        profileName,
        streetNumber,
        streetName,
        APT,
        city,
        state,
        country,
        zipCode,
        Role,
        createdAt,
        Deliver,
        IsVerified,
      } = memberData.result;

      setMemberDetails({
        email,
        profileName,
        streetNumber,
        streetName,
        APT,
        city,
        state,
        country,
        zipCode,
        role: Role?.role || "deliver",
        createdAt,
        delivername: Deliver?.delivername,
        deliverphone: Deliver?.deliverphone,
        delivervehicle: Deliver?.delivervehicle,
        delivervehiclecolor: Deliver?.delivervehiclecolor,
        delivervehiclenumber: Deliver?.delivervehiclenumber,
        deliverlocation: Deliver?.deliverlocation,
        deliverstatus: Boolean(Deliver?.deliverstatus),
        isVerified: Boolean(IsVerified),
      });
    } catch (error) {
      console.error("Error fetching delivery user:", error);
    }
  };

  useEffect(() => {
    loadDetails();
  }, [id]);

  const toggleAvailability = async () => {
    if (!memberDetails) return;
    setSaving(true);
    try {
      const nextStatus = !memberDetails.deliverstatus;
      const response = await authFetch(`/deliver/${id}/status`, {
        method: "PUT",
        body: JSON.stringify({ deliverStatus: nextStatus }),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "Failed to update availability");
      }
      setMemberDetails((prev) => ({ ...prev, deliverstatus: nextStatus }));
    } catch (error) {
      console.error("Error updating deliver status:", error);
    } finally {
      setSaving(false);
    }
  };

  const formatCreatedAt = (createdAt) => {
    if (!createdAt) return "Not available";
    return new Date(createdAt).toLocaleString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    });
  };

  const renderDetails = () => {
    if (!memberDetails) return null;

    const rows = [
      { label: "Profile Name", value: memberDetails.profileName },
      { label: "Email", value: memberDetails.email },
      { label: "Role", value: memberDetails.role },
      { label: "Driver Name", value: memberDetails.delivername },
      { label: "Phone", value: memberDetails.deliverphone },
      { label: "Vehicle", value: memberDetails.delivervehicle },
      { label: "Vehicle Color", value: memberDetails.delivervehiclecolor },
      { label: "Vehicle Number", value: memberDetails.delivervehiclenumber },
      { label: "Location", value: memberDetails.deliverlocation },
      { label: "Street Number", value: memberDetails.streetNumber },
      { label: "Street Name", value: memberDetails.streetName },
      { label: "APT", value: memberDetails.APT },
      { label: "City", value: memberDetails.city },
      { label: "State", value: memberDetails.state },
      { label: "Country", value: memberDetails.country },
      { label: "Zip Code", value: memberDetails.zipCode },
      { label: "Created At", value: formatCreatedAt(memberDetails.createdAt) },
    ];

    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
            <Chip
              label={
                memberDetails.deliverstatus
                  ? "Available for deliveries"
                  : "Unavailable for deliveries"
              }
              color={memberDetails.deliverstatus ? "success" : "warning"}
            />
            <Chip
              label={memberDetails.isVerified ? "Email verified" : "Email not verified"}
              color={memberDetails.isVerified ? "success" : "default"}
            />
          </Box>
          <Button
            variant="contained"
            onClick={toggleAvailability}
            disabled={saving}
            sx={{ mb: 3 }}
          >
            {saving
              ? "Saving..."
              : memberDetails.deliverstatus
              ? "Set Unavailable"
              : "Set Available"}
          </Button>
        </Grid>
        <Grid item xs={12}>
          {rows.map((item) => (
            <Grid container spacing={1} key={item.label} sx={{ marginBottom: 1 }}>
              <Grid item xs={12} sm={4} md={3}>
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  {item.label}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8} md={9}>
                <Typography variant="body1">
                  {item.value || "Not provided"}
                </Typography>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
    );
  };

  return (
    <Box p="20px">
      <Header
        title={`View Delivery Driver ID ${id}`}
        subtitle={
          memberDetails
            ? `${memberDetails.delivername || memberDetails.profileName} — assign orders when Available`
            : "Loading..."
        }
      />
      <Box m="10px 0 0 0" height="55vh" overflow="auto">
        {memberDetails ? renderDetails() : <Typography>Loading...</Typography>}
      </Box>
    </Box>
  );
}

export default ViewDeliver;
